// Arrow-key navigation for a result list that never takes focus.
//
// The input keeps focus and points at the highlighted row with
// `aria-activedescendant` (the combobox pattern). Nothing on this screen may trap
// focus: an agent tabbing past a half-finished lookup with a customer on the line
// must not have to find their way back out (SPEC.md §6).

import { useEffect, useState } from 'react';

export default function useListNavigation(items, { onSelect, onEscape } = {}) {
  const [activeIndex, setActiveIndex] = useState(0);

  // A new result set always starts at the top: keeping index 3 highlighted while
  // the rows underneath change is how an agent picks the wrong customer.
  useEffect(() => {
    setActiveIndex(0);
  }, [items]);

  function handleKeyDown(event) {
    if (event.defaultPrevented) return;
    const count = items.length;

    switch (event.key) {
      case 'ArrowDown':
        if (!count) return;
        event.preventDefault();
        setActiveIndex((index) => (index + 1) % count);
        break;
      case 'ArrowUp':
        if (!count) return;
        event.preventDefault();
        setActiveIndex((index) => (index - 1 + count) % count);
        break;
      case 'Home':
        if (!count) return;
        event.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        if (!count) return;
        event.preventDefault();
        setActiveIndex(count - 1);
        break;
      case 'Enter':
        if (!count) return;
        event.preventDefault();
        onSelect?.(items[Math.min(activeIndex, count - 1)]);
        break;
      case 'Escape':
        onEscape?.(event);
        break;
      default:
        break;
    }
  }

  return { activeIndex, setActiveIndex, handleKeyDown };
}
